import React, { createContext, useContext, useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetBody,
    SheetClose,
    SheetContent,
    SheetHeader,
    type SheetSize,
    SheetTitle,
    useSheetClose,
} from '@/components/ui/sheet';
import { Form, type FormProps, FormSectionInsetContext } from 'widgets';

const SubmitDisabledContext = createContext<(disabled: boolean) => void>(() => {});

type FormSheetContentProps<T extends FieldValues> = Omit<FormProps, 'onSubmit' | 'className' | 'children' | 'title'> & {
    title: React.ReactNode;
    /** close() closes the sheet with its exit animation; call it once the save has succeeded. */
    onSubmit: (values: T, close: () => void) => void;
    submitLabel?: string;
    /** A save is in flight: shows the loading overlay and disables both actions. */
    pending?: boolean;
    submitDisabled?: boolean;
    /** Remounts the form, resetting its values, when it changes. */
    formKey?: React.Key;
    children: React.ReactNode;
};

/**
 * The header, form, scrolling body and Save/Cancel footer of a form sheet, for sheets that also
 * render other states (e.g. loading) inside the same SheetContent. Most callers want FormSheet.
 */
export const FormSheetContent = <T extends FieldValues>({
    title,
    onSubmit,
    submitLabel = 'Save',
    pending = false,
    submitDisabled = false,
    isLoading,
    formKey,
    children,
    ...formProps
}: FormSheetContentProps<T>) => {
    const close = useSheetClose();
    const [disabledByFields, setDisabledByFields] = useState(false);

    return (
        <>
            <SheetHeader variant="pinned">
                <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            <Form
                key={formKey}
                className="flex min-h-0 flex-1 flex-col"
                onSubmit={(values: T) => onSubmit(values, close)}
                isLoading={isLoading || pending}
                {...formProps}
            >
                <SubmitDisabledContext.Provider value={setDisabledByFields}>
                    <FormSectionInsetContext.Provider value={false}>
                        <SheetBody className="flex flex-col gap-6 px-8 pt-6 pb-4">{children}</SheetBody>
                    </FormSectionInsetContext.Provider>
                </SubmitDisabledContext.Provider>
                <div className="flex min-h-[90px] shrink-0 flex-col-reverse gap-4 border-t px-8 py-4 sm:flex-row">
                    <Button
                        className="flex-1"
                        size="lg"
                        type="submit"
                        disabled={pending || submitDisabled || disabledByFields}
                    >
                        {submitLabel}
                    </Button>
                    <SheetClose asChild>
                        <Button className="flex-1" variant="outline" size="lg" type="button" disabled={pending}>
                            Cancel
                        </Button>
                    </SheetClose>
                </div>
            </Form>
        </>
    );
};

type FormSheetProps<T extends FieldValues> = FormSheetContentProps<T> & {
    size?: SheetSize;
    /** Called once the sheet has closed, after its exit animation; unmount the sheet here. */
    onClose: () => void;
};

/** A sheet holding a single form, with the app's standard header, padding and Save/Cancel actions. */
export const FormSheet = <T extends FieldValues>({ size = 'md', onClose, ...contentProps }: FormSheetProps<T>) => (
    <Sheet defaultOpen onExitComplete={onClose}>
        <SheetContent aria-describedby={undefined} layout="scroll-body" size={size}>
            <FormSheetContent {...contentProps} />
        </SheetContent>
    </Sheet>
);

/** Disables Save from inside the form, for conditions that depend on field values. */
export const useDisableFormSheetSubmit = (disabled: boolean) => {
    const setDisabled = useContext(SubmitDisabledContext);

    useEffect(() => {
        setDisabled(disabled);
        return () => setDisabled(false);
    }, [disabled, setDisabled]);
};
